import MoneyCard from "components/MoneyCard";
import {Invoice} from "models/Invoice";
import {AccountableContainer, FilterExternalInvoices, FilterFinalInvoices} from "models/InvoiceContainer";
import React from "react";
import {connect} from "react-redux";
import {RootState} from "store/store";
import {InvoiceState} from 'store/reducers/Invoices';
import {useOfficeTranslation} from "i18n/OfficeTranslation";


interface Props {
    invoiceState: InvoiceState;
    tenantId?: string;
    customerId?: string;
    period?: RevenuePeriod;
}

export const RevenuePeriods = {
    Week: 0,
    Month: 1,
    Quarter: 2,
    Year: 3,
} as const;

export type RevenuePeriod = typeof RevenuePeriods[keyof typeof RevenuePeriods];

interface RevenuePeriodRange {
    start: Date;
    end: Date;
    previousStart: Date;
    previousEnd: Date;
}

const startOfWeek = (date: Date) => {
    const mondayOffset = (date.getDay() + 6) % 7;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - mondayOffset);
};

export const revenuePeriodRange = (period: RevenuePeriod, now = new Date()): RevenuePeriodRange => {
    if (period === RevenuePeriods.Week) {
        const start = startOfWeek(now);
        const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
        const previousStart = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 7);
        return { start, end, previousStart, previousEnd: start };
    }

    if (period === RevenuePeriods.Month) {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const previousStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return { start, end, previousStart, previousEnd: start };
    }

    if (period === RevenuePeriods.Year) {
        const start = new Date(now.getFullYear(), 0, 1);
        const end = new Date(now.getFullYear() + 1, 0, 1);
        const previousStart = new Date(now.getFullYear() - 1, 0, 1);
        return { start, end, previousStart, previousEnd: start };
    }

    const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
    const start = new Date(now.getFullYear(), quarterMonth, 1);
    const end = new Date(now.getFullYear(), quarterMonth + 3, 1);
    const previousStart = new Date(now.getFullYear(), quarterMonth - 3, 1);
    return { start, end, previousStart, previousEnd: start };
};

const RevenueCard: React.FC<Props> = ({ invoiceState, tenantId, customerId, period = RevenuePeriods.Quarter }) => {
    const {t} = useOfficeTranslation();

    let filter = (arg: Invoice) => true
    if (tenantId !== undefined) {
        filter = (invoice: Invoice) => invoice.tenant === tenantId
    }
    if (customerId !== undefined) {
        filter = (invoice: Invoice) => invoice.customer === customerId
    }

    const now = new Date();
    const range = revenuePeriodRange(period, now);
    const percentageInPeriod = (now.getTime() - range.start.getTime()) / (range.end.getTime() - range.start.getTime()) * 100;
    const invoices = new AccountableContainer<Invoice>(invoiceState.getInvoices().filter(filter));

    const amountThisPeriod = invoices
        .Filter(invoice => invoice.GetCreatedDate() >= range.start && invoice.GetCreatedDate() < range.end)
        .Filter(FilterExternalInvoices)
        .Filter(FilterFinalInvoices)
        .CalcTotal(invoice => invoice.CalcTotalContents());

    const amountPreviousPeriod = invoices
        .Filter(invoice => invoice.GetCreatedDate() >= range.previousStart && invoice.GetCreatedDate() < range.previousEnd)
        .Filter(FilterExternalInvoices)
        .Filter(FilterFinalInvoices)
        .CalcTotal(invoice => invoice.CalcTotalContents()) / 100 * percentageInPeriod;

    const percentage = ((amountThisPeriod - amountPreviousPeriod) / amountPreviousPeriod) * 100;
    const previousPeriodLabel = period === RevenuePeriods.Week
        ? t("dashboard.revenueCard.previousWeek")
        : period === RevenuePeriods.Month
            ? t("dashboard.revenueCard.previousMonth")
            : period === RevenuePeriods.Year
                ? t("dashboard.revenueCard.previousYear")
                : t("common.lastQCard.lastQuarter");

    return (
        <MoneyCard
            title={t("common.revenue")}
            amount={amountThisPeriod}
            timeSpan={previousPeriodLabel}
            percentage={isFinite(percentage) ? percentage : 100}
            trendUp={percentage > 0}
            icon={`mdi mdi-cash-multiple text-primary`}
        />
    );

}

const mapStateToProps = (state: RootState) => {
    return {
        invoiceState: state.Invoices
    };
};

export default connect(mapStateToProps)(RevenueCard);
